import { useEffect, useSyncExternalStore } from "react"

export type Theme = "system" | "light" | "dark"

const STORAGE_KEY = "platonica_theme"
const DEFAULT_THEME: Theme = "system"

function isTheme(value: unknown): value is Theme {
  return value === "system" || value === "light" || value === "dark"
}

function getStoredTheme(): Theme {
  if (typeof window === "undefined") return DEFAULT_THEME
  const raw = window.localStorage.getItem(STORAGE_KEY)
  return isTheme(raw) ? raw : DEFAULT_THEME
}

function resolveEffective(theme: Theme): "light" | "dark" {
  if (theme === "light" || theme === "dark") return theme
  if (typeof window === "undefined") return "light"
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

function applyToDocument(effective: "light" | "dark") {
  if (typeof document === "undefined") return
  const root = document.documentElement
  root.classList.toggle("dark", effective === "dark")
  root.dataset.theme = effective
}

const subscribers = new Set<() => void>()

function notify() {
  for (const cb of subscribers) cb()
}

/** Local-only setter. UI surfaces use `useTheme` which composes this with the
 * backend persistence via the AuthContext. */
export function setThemeLocal(next: Theme) {
  if (typeof window === "undefined") return
  window.localStorage.setItem(STORAGE_KEY, next)
  applyToDocument(resolveEffective(next))
  notify()
}

export function getTheme(): Theme {
  return getStoredTheme()
}

/** Reconcile localStorage with the authenticated professor's `settings.theme`
 * — called by the AuthContext when a profile is loaded. The professor's saved
 * preference wins on first sync; subsequent local changes flow through
 * `setThemeLocal` and are pushed back to the backend by the caller. */
export function syncThemeFromProfessor(theme: Theme | undefined) {
  if (!theme || !isTheme(theme)) return
  const current = getStoredTheme()
  if (current === theme) return
  setThemeLocal(theme)
}

export function applyInitialTheme() {
  applyToDocument(resolveEffective(getStoredTheme()))
}

function subscribe(callback: () => void) {
  subscribers.add(callback)
  return () => {
    subscribers.delete(callback)
  }
}

/** Read-only hook — the active theme value, reactive across components.
 * Use `useThemeSetter` (from AuthContext) to mutate; that path persists to
 * the backend in addition to localStorage. */
export function useTheme(): Theme {
  return useSyncExternalStore(
    subscribe,
    () => getStoredTheme(),
    () => DEFAULT_THEME,
  )
}

/** Subscribes to OS-level color-scheme changes when the active mode is
 * "system". Mount once near the root. */
export function useSystemThemeListener() {
  const theme = useTheme()
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return
    const mql = window.matchMedia("(prefers-color-scheme: dark)")
    const onChange = () => applyToDocument(resolveEffective("system"))
    mql.addEventListener("change", onChange)
    return () => mql.removeEventListener("change", onChange)
  }, [theme])
}
